<script setup>
import {computed, ref, watch} from "vue";
import {mdiClose, mdiMagnify} from "@mdi/js";
import {useDisplay} from "vuetify";
import {pgettext} from "@/utils/translate";

const dialog = defineModel({type: Boolean});

const props = defineProps({
    countries: {
        type: Array,
        required: true,
    },
});
const emit = defineEmits(['select']);

// Fullscreen on a phone, a normal centred dialog on a ZOS desktop where a
// fullscreen takeover would be jarring.
const {mobile} = useDisplay();

const search = ref('');

// Strip diacritics on both sides so "cesko" matches "Česko" and "nemecko" matches
// "Německo". Nobody reaches for diacritics one-handed at an incident.
function normalize(value) {
    return (value || '')
        .toString()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
}

const filtered = computed(() => {
    const needle = normalize(search.value);
    if (!needle) {
        return props.countries;
    }
    return props.countries.filter((country) => normalize(country.name).includes(needle));
});

// Start from a clean query every time it opens, so a stale filter never hides
// the country the operator is looking for.
watch(dialog, (isOpen) => {
    if (isOpen) {
        search.value = '';
    }
});

function select(country) {
    emit('select', country.code);
    dialog.value = false;
}
</script>

<template>
    <v-dialog v-model="dialog" :fullscreen="mobile" :max-width="mobile ? undefined : 560" scrollable>
        <v-card>
            <v-toolbar color="primary" density="comfortable">
                <v-btn :icon="mdiClose"
                       :aria-label="pgettext('action', 'Close')"
                       @click="dialog = false"></v-btn>
                <v-text-field v-model="search"
                              autofocus
                              hide-details
                              single-line
                              variant="plain"
                              class="mr-2"
                              :prepend-inner-icon="mdiMagnify"
                              :placeholder="pgettext('label', 'Search country')"></v-text-field>
            </v-toolbar>
            <v-card-text class="pa-0">
                <v-list v-if="filtered.length" density="comfortable">
                    <v-list-item v-for="country in filtered"
                                 :key="country.code"
                                 :title="country.name"
                                 min-height="48"
                                 @click="select(country)"></v-list-item>
                </v-list>
                <div v-else class="pa-6 text-center text-medium-emphasis">
                    {{ pgettext('message', 'No country matches your search') }}
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>
